'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { cn } from '../../utils/tailwindUtils';
import { Button } from './button';
import { Input } from './input';
import useApiMethods from '../../lib/useApiMethods';
import { getCSVData, getPrintData } from '../common/CommonFunctions';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import ToastMsg from '../common/ToastMsg';

// --- Reusable SVG Icons ---

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-gray-400"><path d="m21 21-4.3-4.3" /><circle cx="11" cy="11" r="8" /></svg>
);

const FilterIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
);

const ExcelIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M8 13h2" /><path d="M14 13h2" /><path d="M8 17h2" /><path d="M14 17h2" /></svg>
);

const PDFIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6" /><rect x="6" y="14" width="12" height="8" rx="1" /></svg>
);

const DraftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" /></svg>
);

const SortIcon = ({ direction }) => {
    if (direction === 'none') {
        return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 ml-2 opacity-30"><path d="m7 15-3 3 3 3" /><path d="m20 9-3-3-3 3" /><path d="M4 18v-6a4 4 0 0 1 4-4h12" /><path d="M17 6v6a4 4 0 0 1-4 4H4" /></svg>;
    }
    if (direction === 'asc') {
        return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 ml-2"><path d="m3 16 4 4 4-4" /><path d="M7 20V4" /><path d="m21 8-4-4-4 4" /><path d="M17 4v16" /></svg>;
    }
    return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 ml-2"><path d="m3 8 4-4 4 4" /><path d="M7 4v16" /><path d="m21 16-4 4-4-4" /><path d="M17 20V4" /></svg>;
};

const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m15 18-6-6 6-6" /></svg>
);

const ChevronRightIcon = ({ className = "" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`h-5 w-5 ${className}`}><path d="m9 18 6-6-6-6" /></svg>
);

const ChevronsLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m11 17-5-5 5-5" /><path d="m18 17-5-5 5-5" /></svg>
);

const ChevronsRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m6 17 5-5-5-5" /><path d="m13 17 5-5-5-5" /></svg>
);

const EmptyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="m16 22-1-4" /><path d="M19 13.99a1 1 0 0 0 1-1V12a2 2 0 0 0-2-2h-3a1 1 0 0 1-1-1V4a2 2 0 0 0-4 0v5a1 1 0 0 1-1 1H6a2 2 0 0 0-2 2v.99a1 1 0 0 0 1 1" /><path d="M5 14h14l1.973 6.767A1 1 0 0 1 20 22H4a1 1 0 0 1-.973-1.233z" /><path d="m8 22 1-4" /></svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
);

// --- Right Sidebar Filter Component ---
export const FilterSidebar = ({
    isOpen,
    onClose,
    filterableColumns,
    tempFilters,
    setTempFilters,
    onApply,
    onClear
}) => {
    return (
        <div
            className={cn(
                "fixed top-0 right-0 h-full w-80 shadow-lg transform transition-transform duration-300 ease-in-out z-50 border-l",
                "bg-white border-gray-200 text-gray-800", // light theme
                "dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100", // dark theme
                isOpen ? "translate-x-0" : "translate-x-full"
            )}
        >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold">Filter Data</h3>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                >
                    <CloseIcon />
                </button>
            </div>

            {/* Content */}
            <div className="p-4 h-[calc(100%-120px)] overflow-y-auto">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Filter data by specific columns.
                </p>
                <div className="space-y-4">
                    {filterableColumns.map(({ accessor, Header, filterType }) => (
                        <div key={accessor} className="flex flex-col gap-2">
                            <label
                                htmlFor={accessor}
                                className="text-xs font-medium text-gray-700 dark:text-gray-300"
                            >
                                {Header}
                            </label>

                            {accessor === "is_active" ? (
                                // Custom filter for Status
                                <div className="flex gap-2">
                                    <button
                                        onClick={() =>
                                            setTempFilters((prev) => ({ ...prev, is_active: "True" }))
                                        }
                                        className={`px-3 py-1 rounded text-xs font-medium border transition-colors
            ${tempFilters["is_active"] === "True"
                                                ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900 dark:text-green-300"
                                                : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                                            }`}
                                    >
                                        Active
                                    </button>
                                    <button
                                        onClick={() =>
                                            setTempFilters((prev) => ({ ...prev, is_active: "False" }))
                                        }
                                        className={`px-3 py-1 rounded text-xs font-medium border transition-colors
            ${tempFilters["is_active"] === "False"
                                                ? "bg-red-100 text-red-700 border-red-300 dark:bg-red-900 dark:text-red-300"
                                                : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                                            }`}
                                    >
                                        Inactive
                                    </button>
                                </div>
                            ) : filterType === 'date' ? (
                                // Date Picker for date fields
                                <DatePicker
                                    value={tempFilters[accessor] ? dayjs(tempFilters[accessor]) : null}
                                    onChange={(dateObj, dateString) => {
                                        setTempFilters((prev) => ({
                                            ...prev,
                                            [accessor]: dateString || ""
                                        }));
                                    }}
                                    format="YYYY-MM-DD"
                                    className="w-full h-9 placeholder:text-xs"
                                    placeholder={`Select ${Header}...`}
                                />
                            ) : filterType === 'range' ? (
                                // Range Picker for date ranges
                                <DatePicker.RangePicker
                                    value={[
                                        tempFilters[`${accessor}_from`] ? dayjs(tempFilters[`${accessor}_from`]) : null,
                                        tempFilters[`${accessor}_to`] ? dayjs(tempFilters[`${accessor}_to`]) : null
                                    ]}
                                    onChange={(dates, dateStrings) => {
                                        setTempFilters((prev) => ({
                                            ...prev,
                                            [`${accessor}_from`]: dateStrings[0] || "",
                                            [`${accessor}_to`]: dateStrings[1] || ""
                                        }));
                                    }}
                                    format="YYYY-MM-DD"
                                    className="w-full h-9 placeholder:text-xs"
                                    placeholder={['Start date', 'End date']}
                                />
                            ) : filterType === 'datetime' ? (
                                // Date Time Picker for datetime fields
                                <DatePicker
                                    value={tempFilters[accessor] ? dayjs(tempFilters[accessor]) : null}
                                    onChange={(dateObj, dateString) => {
                                        setTempFilters((prev) => ({
                                            ...prev,
                                            [accessor]: dateString || ""
                                        }));
                                    }}
                                    format="YYYY-MM-DD HH:mm"
                                    showTime={{ format: 'HH:mm' }}
                                    className="w-full h-9 placeholder:text-xs"
                                    placeholder={`Select ${Header}...`}
                                />
                            ) : filterType === 'requestStatus' ? (
                                // Custom filter for request status
                                <div className="flex gap-2 flex-wrap">
                                    {['pending', 'approved', 'rejected', 'cancelled'].map((status) => (
                                        <button
                                            key={status}
                                            onClick={() =>
                                                setTempFilters((prev) => ({
                                                    ...prev,
                                                    [accessor]: status
                                                }))
                                            }
                                            className={`px-3 py-1 rounded text-xs font-medium border transition-colors capitalize
                                                ${tempFilters[accessor] === status
                                                    ? status === 'pending'
                                                        ? "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-300"
                                                        : status === 'approved'
                                                            ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900 dark:text-green-300"
                                                            : status === 'rejected'
                                                                ? "bg-red-100 text-red-700 border-red-300 dark:bg-red-900 dark:text-red-300"
                                                                : "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-300"
                                                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                                                }`}
                                        >
                                            {status}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                // Default Input for others
                                <Input
                                    id={accessor}
                                    value={tempFilters[accessor] || ""}
                                    onChange={(e) =>
                                        setTempFilters((prev) => ({
                                            ...prev,
                                            [accessor]: e.target.value,
                                        }))
                                    }
                                    placeholder={`Filter by ${Header}...`}
                                    className="h-9 placeholder:text-xs"
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                <div className="flex justify-end gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onClear}
                        className="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                    >
                        Clear All
                    </Button>
                    <Button
                        size="sm"
                        onClick={onApply}
                        className={cn(
                            'bg-sidebar hover:bg-sidebar-primary',
                            'dark:bg-indigo-600 dark:hover:bg-indigo-700',
                            'text-white transition-colors duration-200'
                        )}
                    >
                        Apply Filters
                    </Button>
                </div>
            </div>
        </div>
    );
};

// --- TableHeader Component ---
export const TableHeader = ({
    pagename,
    tableTitle,
    globalFilter,
    setGlobalFilter,
    children,
    filterableColumns,
    columnFilters,
    onColumnFilterChange,
    onApplyFilters,
    onClearFilters,
    sorting,
    isDraft,
    setIsDraft,
    url,
    isDateRequired = false,
    isCSVRequired = false,
    isPDFRequired = false,
    isDraftRequired = false,
    isMonthYearRequired = false,
    isFromToDateRequired = false,
    date,
    setDate,
    month,
    setMonth,
    year,
    setYear,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    printFilter,
    extras = {},
}) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState(columnFilters);

    const isFilterActive = useMemo(() => Object.values(columnFilters).some(Boolean), [columnFilters]);

    const handleApply = () => {
        onApplyFilters(tempFilters);
        setSidebarOpen(false);
    };

    const handleClear = () => {
        setTempFilters({});
        onClearFilters();
    };

    useEffect(() => {
        setTempFilters(columnFilters);
    }, [columnFilters]);

    const handleExport = (type) => {
        const filters = {
            ...printFilter,
            search: globalFilter || "",
            ...columnFilters,
            column: sorting?.accessor || "",
            sort: sorting?.direction !== "none" ? sorting.direction : "",
            date: date ? dayjs(date).format("YYYY-MM-DD") : "",
            month: month ? dayjs(month).format("M") : "",
            year: year ? dayjs(year).format("YYYY") : "",
        };

        if (isMonthYearRequired) {
            delete filters['date']
        }

        if (isFromToDateRequired) {
            delete filters['date']
            delete filters['month']
            delete filters['year']
        }

        if (!isDateRequired && !isMonthYearRequired && !isFromToDateRequired) {
            delete filters['date']
            delete filters['month']
            delete filters['year']
        }

        switch (type) {
            case "pdf":
                getPrintData(url, filters);
                break;
            case "csv":
            case "xlsx":
                getCSVData(url, filters, type);
                break;
            default:
                console.warn(`Unsupported export type: ${type}`);
        }
    };

    const toggleDraft = () => {
        setIsDraft((prev) => !prev);
    };

    // Handle status button click - directly update column filters
    const handleStatusClick = (status) => {
        onApplyFilters({
            ...columnFilters,
            approval_status: status
        });
    };

    // Clear status filter
    const handleClearStatus = () => {
        const newFilters = { ...columnFilters };
        delete newFilters.approval_status;
        onApplyFilters(newFilters);
    };

    // Check if any status filter is active
    const isStatusFilterActive = columnFilters.approval_status;

    return (
        <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
            {children && <div className=" mb-2">{children}</div>}
            {/* <h2 className="text-xl font-semibold text-gray-800">{tableTitle}</h2> --> Title moved to parent component */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex md:flex-row items-center gap-2 w-full md:w-auto">
                    {/* Excel Button - Green theme */}
                    {
                        isCSVRequired && (
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        className="flex items-center justify-center w-10 h-10 rounded-md shadow-sm text-xs font-medium
                                bg-green-50 text-green-700 border border-green-200 
                                hover:bg-green-100 hover:border-green-300 hover:text-green-800
                                transition-colors duration-200
                                dark:bg-green-900/30 dark:text-green-300 dark:border-green-700
                                dark:hover:bg-green-900/50 dark:hover:text-green-200 dark:hover:border-green-600"
                                        onClick={() => handleExport('csv')}
                                    >
                                        <ExcelIcon />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Export to CSV
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    {/* PDF Button - Red theme */}
                    {
                        isPDFRequired && (
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        className="flex items-center justify-center w-10 h-10 rounded-md shadow-sm text-xs font-medium
                                bg-red-50 text-red-700 border border-red-200 
                                hover:bg-red-100 hover:border-red-300 hover:text-red-800
                                transition-colors duration-200
                                dark:bg-red-900/30 dark:text-red-300 dark:border-red-700
                                dark:hover:bg-red-900/50 dark:hover:text-red-200 dark:hover:border-red-600"
                                        onClick={() => handleExport("pdf")}
                                    >
                                        <PDFIcon />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Export to PDF
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    {/* Draft Button - Blue theme */}
                    {
                        isDraftRequired && (
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        className={cn("flex items-center justify-center w-10 h-10 rounded-md shadow-sm text-xs font-medium",
                                            "bg-blue-50 text-blue-700 border border-blue-200",
                                            "hover:bg-blue-100 hover:border-blue-300 hover:text-blue-800",
                                            "transition-colors duration-200",
                                            "dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700",
                                            "dark:hover:bg-blue-900/50 dark:hover:text-blue-200 dark:hover:border-blue-600"
                                            , isDraft ? "ring-2 ring-offset-2 ring-blue-500" : "")}
                                        onClick={toggleDraft}
                                    >
                                        <DraftIcon />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Draft
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    {pagename === 'approval inbox' && (
                        <div className='flex gap-2 items-center border-l px-2 border-gray-200'>
                            {/* Clear Filter Button (when any status is active) */}
                            {isStatusFilterActive && (
                                <button
                                    onClick={handleClearStatus}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200"
                                >
                                    Clear Filter
                                </button>
                            )}

                            {/* Status Buttons */}
                            {['pending', 'approved', 'rejected', 'cancelled'].map((status) => (
                                <button
                                    key={status}
                                    onClick={() => handleStatusClick(status)}
                                    className={cn(
                                        'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer',
                                        columnFilters.approval_status === status
                                            ? status === 'pending'
                                                ? 'bg-yellow-100 text-yellow-700 border-yellow-300 scale-110'
                                                : status === 'approved'
                                                    ? 'bg-green-100 text-green-700 border-green-300 scale-110'
                                                    : status === 'rejected'
                                                        ? 'bg-red-100 text-red-700 border-red-300 scale-110'
                                                        : 'bg-gray-100 text-gray-600 border-gray-300 scale-110'
                                            : status === 'pending'
                                                ? 'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-100'
                                                : status === 'approved'
                                                    ? 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100'
                                                    : status === 'rejected'
                                                        ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                                                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                    )}
                                >
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                    {extras[status] ? ` (${extras[status]})` : ' (0)'}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex md:flex-row items-center gap-2 w-full md:w-auto">
                    {/* date */}
                    {
                        // based on the isMonthYearRequired or isDateRequired we need to change the format of a datePicker like normal and year and month
                        isDateRequired ? (
                            <DatePicker
                                value={date ? dayjs(date) : null} // AntD expects a dayjs object
                                onChange={(dateObj, dateString) => setDate(dateString)} // dateString = "YYYY-MM-DD"
                                format="YYYY-MM-DD"
                                picker={'date'}
                                className='w-32'
                            // disabledDate={(current) => {
                            //     // Disable past dates : hide for later use
                            //     return current && current < dayjs().startOf("day");
                            // }}
                            />
                        ) :

                            isMonthYearRequired ? (
                                <>
                                    {/* Month Picker */}
                                    <DatePicker
                                        picker="month"
                                        value={month}
                                        onChange={(dateObj) => setMonth(dateObj)}
                                        format="MMM"
                                        className="w-20"
                                    />

                                    {/* Year Picker */}
                                    <DatePicker
                                        picker="year"
                                        value={year}
                                        onChange={(dateObj) => setYear(dateObj)}
                                        format="YYYY"
                                        className="w-20"
                                    />
                                </>
                            ) :
                                isFromToDateRequired ? (
                                    <>
                                        {/* From Date Picker */}
                                        <DatePicker
                                            picker="date"
                                            value={fromDate}
                                            onChange={(dateObj) => setFromDate(dateObj)}
                                            format="YYYY-MM-DD"
                                            className="w-32"
                                        />

                                        {/* To Date Picker */}
                                        <DatePicker
                                            picker="date"
                                            value={toDate}
                                            onChange={(dateObj) => setToDate(dateObj)}
                                            format="YYYY-MM-DD"
                                            className="w-32"
                                        />
                                    </>
                                ) : ''
                    }
                    <div className="relative w-full md:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon />
                        </div>
                        <input
                            type="text"
                            value={globalFilter || ''}
                            onChange={e => setGlobalFilter(e.target.value)}
                            className="block w-full pl-10 pr-3 py-3 border rounded-md leading-5 
                            bg-white text-gray-900 placeholder-gray-500 
                            focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-sidebar focus:border-sidebar-primary 
                            sm:text-xs dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400"
                            placeholder="Search table..."
                        />
                    </div>
                    {filterableColumns.length > 0 && (
                        <>
                            <Tooltip>
                                <TooltipTrigger>
                                    <button
                                        onClick={() => setSidebarOpen(true)}
                                        className="relative flex items-center justify-center w-10 h-10 border rounded-md shadow-sm 
                                        text-xs font-medium 
                                        text-gray-700 bg-white hover:bg-gray-50 border-gray-300
                                        dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600"
                                    >
                                        <FilterIcon />
                                        {isFilterActive && (
                                            <span className="absolute -top-1 -right-1 block h-2.5 w-2.5 rounded-full bg-indigo-600 ring-2 ring-white" />
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} transition={false} className="text-xs">
                                    Filter
                                </TooltipContent>
                            </Tooltip>

                            <FilterSidebar
                                isOpen={sidebarOpen}
                                onClose={() => setSidebarOpen(false)}
                                filterableColumns={filterableColumns}
                                tempFilters={tempFilters}
                                setTempFilters={setTempFilters}
                                onApply={handleApply}
                                onClear={handleClear}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- DataTable Component (Renamed to Reactable) ---
export const Reactable = ({
    pagename = "",
    columns,
    data,
    selectedDate,
    selectedMonth,
    setData,
    setTotalCount,
    filter = {},
    setFetchFilter = () => { },
    tableTitle = "Data",
    renderSubComponent,
    children,
    tableHeight,
    rowSize = 'md',
    onRowClick,
    onRowDoubleClick,
    onRowHighlightKey = null,
    url,
    serverSide = false,
    serverTrigger = false,
    isDateRequired = false,
    isCSVRequired = false,
    isPDFRequired = false,
    isDraftRequired = false,
    isMonthYearRequired = false,
    isFromToDateRequired = false,
    manualPagination = false,
    activeTab,
    printFilter,
}) => {
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [sorting, setSorting] = useState({ accessor: '', direction: 'none' });
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [jumpToPage, setJumpToPage] = useState('');
    const [expandedRows, setExpandedRows] = useState({});
    const [totalRecords, setTotalRecords] = useState(0);
    const [isDraft, setIsDraft] = useState(false);
    const [loading, setLoading] = useState(false);

    const [date, setDate] = useState(new Date().toISOString().split("T")[0]);

    // State
    const [month, setMonth] = useState(
        selectedMonth ? selectedMonth : dayjs()
    );
    const [year, setYear] = useState(dayjs());

    const [fromDate, setFromDate] = useState(
        selectedDate ? selectedDate?.from_date : dayjs()
    );
    const [toDate, setToDate] = useState(
        selectedDate ? selectedDate?.to_date : dayjs()
    );
    const [extras, setExtras] = useState({})

    const { fetch } = useApiMethods();
    const filterableColumns = useMemo(() => columns.filter(c => c.filterable), [columns]);

    // Function to determine row highlight class
    const getRowHighlightClass = (row) => {

        if (!onRowHighlightKey) return '';

        let shouldHighlight = false;

        if (typeof onRowHighlightKey === 'function') {
            // Custom function provided
            shouldHighlight = onRowHighlightKey(row);
        } else {
            // String key provided - check if the value is truthy
            shouldHighlight = !!row[onRowHighlightKey];
        }

        return shouldHighlight
            ? 'bg-green-100 dark:bg-green-900'
            : '';
    };

    useEffect(() => {
        if (selectedMonth && isMonthYearRequired) {
            setMonth(selectedMonth);
            setYear(selectedMonth);
        }
    }, [selectedMonth, isMonthYearRequired]);

    // Server-side data fetching
    useEffect(() => {
        if (serverSide && url) {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    setData([]); // Clear existing data
                    setExtras({})
                    setTotalRecords(0);
                    setTotalCount(0);

                    // Build filters object for API
                    const filters = {
                        search: globalFilter || "",
                        ...filter,
                        ...columnFilters,
                        column: sorting.accessor || "",
                        sort: sorting.direction !== 'none' ? sorting.direction : "",
                        page_no: pagination.pageIndex + 1,
                        page_size: pagination.pageSize,
                        is_draft: isDraft ? "True" : "",
                        date: date ? dayjs(date).format("YYYY-MM-DD") : "",
                        month: month ? dayjs(month).format("M") : "",
                        year: year ? dayjs(year).format("YYYY") : "",
                        from_date: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : "",
                        to_date: toDate ? dayjs(toDate).format('YYYY-MM-DD') : "",
                    };

                    // temp workaround for payslip month and year filter
                    if (pagename == "payslips") {
                        delete filters['month']
                        delete filters['year']
                        filters['payroll_month'] = month ? dayjs(month).format("M") : "";
                        filters['payroll_year'] = year ? dayjs(year).format("YYYY") : "";
                    }

                    if (isMonthYearRequired) {
                        delete filters['date']
                        delete filters['from_date']
                        delete filters['to_date']
                    }

                    if (isFromToDateRequired) {
                        delete filters['date']
                        delete filters['month']
                        delete filters['year']
                    }

                    if (isDateRequired) {
                        delete filters['from_date']
                        delete filters['to_date']
                    }

                    if (!isDateRequired && !isMonthYearRequired && !isFromToDateRequired) {
                        delete filters['date']
                        delete filters['month']
                        delete filters['year']
                        delete filters['from_date']
                        delete filters['to_date']
                    }

                    // Remove empty filters
                    Object.keys(filters).forEach(key => {
                        if (filters[key] === "" || filters[key] === null || filters[key] === undefined) {
                            delete filters[key];
                        }
                    });

                    const response = await fetch(url, filters);

                    if (response?.data?.result) {
                        setData(response.data.result);
                        setExtras(response.data.extras)
                        setTotalRecords(response.data.total_count || 0);
                        setTotalCount(response.data.total_request_count || response.data.total_count || 0);
                        setFetchFilter(filters)
                    }

                    if (response.status === 'fail') {
                        ToastMsg('error', response.message || 'Failed to fetch data.');
                    }
                } catch (err) {
                    console.error("Reactable API Error:", err);
                } finally {
                    setLoading(false);
                }
            };

            // Add debounce to prevent too many API calls
            const timeoutId = setTimeout(fetchData, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [
        url,
        serverSide,
        serverTrigger,
        globalFilter,
        setTotalCount,
        columnFilters,
        sorting,
        pagination,
        setData,
        isDraft,
        date,
        month,
        year,
        fromDate,
        toDate
    ]);

    useEffect(() => {
        setExtras({})
        setData([])
    }, [activeTab])

    // Client-side filtering and sorting (for non-serverSide mode)
    const processedData = useMemo(() => {
        if (serverSide) {
            return data; // Use data directly from API when serverSide is true
        }

        let filtered = [...data];

        // Global filter
        if (globalFilter) {
            filtered = filtered.filter(row =>
                columns.some(({ accessor }) => {
                    const value = row[accessor];
                    return String(value).toLowerCase().includes(globalFilter.toLowerCase());
                })
            );
        }

        // Column filters
        Object.entries(columnFilters).forEach(([accessor, filterValue]) => {
            if (filterValue) {
                filtered = filtered.filter(row => {
                    const value = row[accessor];
                    return String(value).toLowerCase().includes(filterValue.toLowerCase());
                });
            }
        });

        // Sorting
        if (sorting.direction !== 'none') {
            filtered = [...filtered].sort((a, b) => {
                const aValue = a[sorting.accessor];
                const bValue = b[sorting.accessor];
                if (aValue < bValue) return sorting.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sorting.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [data, columns, globalFilter, columnFilters, sorting, serverSide]);

    // Calculate page count based on server-side or client-side
    const pageCount = serverSide
        ? Math.ceil(totalRecords / pagination.pageSize)
        : Math.ceil(processedData.length / pagination.pageSize);

    // Get data for current page
    const paginatedData = useMemo(() => {
        if (serverSide && !manualPagination) {
            return data; // API already returns paginated data
        }

        const start = pagination.pageIndex * pagination.pageSize;
        const end = start + pagination.pageSize;
        return processedData?.slice(start, end);
    }, [processedData, pagination, serverSide, data, manualPagination]);

    const handleSort = (accessor) => {
        setSorting(prev => {
            if (prev.accessor === accessor) {
                if (prev.direction === 'asc') return { accessor, direction: 'desc' };
                if (prev.direction === 'desc') return { accessor, direction: 'none' };
            }
            return { accessor, direction: 'asc' };
        });
    };

    const handleJumpToPage = (e) => {
        e.preventDefault();
        const page = parseInt(jumpToPage, 10);
        if (!isNaN(page) && page > 0 && page <= pageCount) {
            setPagination(p => ({ ...p, pageIndex: page - 1 }));
        }
        setJumpToPage('');
    };

    const handlePageSizeChange = (e) => {
        setPagination(p => ({
            ...p,
            pageSize: Number(e.target.value),
            pageIndex: 0
        }));
    };

    const toggleRow = (index) => {
        setExpandedRows(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const getAlignmentClass = (align) => {
        switch (align) {
            case 'center': return 'text-center';
            case 'right': return 'text-right';
            default: return 'text-left';
        }
    };

    const getRowSizeClass = () => {
        switch (rowSize) {
            case 'sm': return 'py-2';
            case 'md': return 'py-4';
            case 'lg': return 'py-6';
            default: return 'py-4';
        }
    }

    // Reset to first page when filters change (for client-side only)
    useEffect(() => {
        if (!serverSide) {
            setPagination(p => ({ ...p, pageIndex: 0 }));
        }
    }, [globalFilter, columnFilters, sorting, serverSide]);

    return (
        <div className="shadow-lg rounded-lg overflow-hidden border border-gray-200 bg-white flex flex-col dark:bg-gray-900">
            {/* Header */}
            <TableHeader
                pagename={pagename}
                tableTitle={tableTitle}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                filterableColumns={filterableColumns}
                columnFilters={columnFilters}
                onApplyFilters={setColumnFilters}
                onClearFilters={() => setColumnFilters({})}
                sorting={sorting}
                isDraft={isDraft}
                setIsDraft={setIsDraft}
                url={url}
                isDateRequired={isDateRequired}
                isCSVRequired={isCSVRequired}
                isPDFRequired={isPDFRequired}
                isDraftRequired={isDraftRequired}
                isMonthYearRequired={isMonthYearRequired}
                isFromToDateRequired={isFromToDateRequired}
                date={date}
                setDate={setDate}
                month={month}
                setMonth={setMonth}
                year={year}
                setYear={setYear}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                extras={extras}
                printFilter={printFilter}
            >
                {children}
            </TableHeader>

            {/* Loader */}
            {
                loading ? (
                    <div
                        className="flex justify-center items-center py-8 bg-white dark:bg-gray-900"
                        style={tableHeight ? { height: tableHeight } : {}}
                    >
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
                    </div>
                ) : (
                    <div
                        className="overflow-x-auto bg-white dark:bg-gray-900"
                        style={tableHeight ? { height: tableHeight, overflowY: "auto" } : {}}
                    >
                        {/* Table */}
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    {renderSubComponent && (
                                        <th className="sticky top-0 z-20 w-12 bg-gray-50 dark:bg-gray-800"></th>
                                    )}
                                    {columns.map(({ accessor, Header, sortable, fixed, align, width, maxWidth }) => (
                                        <th
                                            key={accessor}
                                            scope="col"
                                            className={cn(
                                                "px-3 sm:px-6 py-3 capitalize tracking-wider font-medium text-gray-500 dark:text-gray-300 text-xs sticky top-0 z-0 bg-gray-100 dark:bg-gray-800 whitespace-nowrap", // ⬅ prevents wrapping
                                                getAlignmentClass(align),
                                                fixed && "z-[9] sticky",
                                                fixed && (align === "right" ? "right-0" : "left-0"),
                                                fixed && renderSubComponent && align !== "right" && "left-12",
                                            )}
                                            style={{
                                                width: width || undefined,
                                                maxWidth: maxWidth || undefined,
                                                minWidth: width || undefined
                                            }}
                                        >
                                            {sortable ? (
                                                <button
                                                    onClick={() => handleSort(accessor)}
                                                    className={cn(
                                                        "flex items-center gap-1 focus:outline-none",
                                                        align === "center"
                                                            ? "mx-auto"
                                                            : align === "right"
                                                                ? "ml-auto"
                                                                : "mr-auto"
                                                    )}
                                                >
                                                    <span>{Header}</span>
                                                    <SortIcon
                                                        direction={
                                                            sorting.accessor === accessor
                                                                ? sorting.direction
                                                                : "none"
                                                        }
                                                    />
                                                </button>
                                            ) : (
                                                Header
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                {paginatedData?.length > 0 ? (
                                    paginatedData.map((row, i) => {
                                        const highlightClass = getRowHighlightClass(row);

                                        const isFirstNonFixedColumn = (index) => {
                                            const nonFixedColumns = columns.filter(col => !col.fixed);
                                            return index === (renderSubComponent ? 1 : 0) && nonFixedColumns.length > 0;
                                        };

                                        return (
                                            <React.Fragment key={i}>
                                                <tr
                                                    className={cn(
                                                        "group transition-colors duration-150",
                                                        highlightClass || "hover:bg-gray-50 dark:hover:bg-gray-800"
                                                    )}
                                                    onClick={() => onRowClick?.(row)}
                                                    onDoubleClick={() => onRowDoubleClick?.(row)}
                                                >
                                                    {renderSubComponent && (
                                                        <td className={cn(
                                                            "sticky left-0 z-20 px-3 sm:px-6 whitespace-nowrap text-xs text-gray-700 dark:text-gray-300",
                                                            highlightClass ? highlightClass.replace('border-l-4', '') : "bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800"
                                                        )}>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleRow(i);
                                                                }}
                                                            >
                                                                <ChevronRightIcon
                                                                    className={`transform transition-transform ${expandedRows[i] ? "rotate-90" : ""
                                                                        }`}
                                                                />
                                                            </button>
                                                        </td>
                                                    )}
                                                    {columns.map(({ accessor, Cell, fixed, align }, colIndex) => (
                                                        <td
                                                            key={accessor}
                                                            className={cn(
                                                                "px-3 sm:px-6 whitespace-nowrap text-[13px] text-gray-700 dark:text-gray-300",
                                                                getRowSizeClass(),
                                                                getAlignmentClass(align),
                                                                fixed && "sticky z-0 ", highlightClass ? highlightClass.replace('border-l-4', '') : "group-hover:bg-gray-50 dark:group-hover:bg-gray-800",
                                                                fixed && (align === "right" ? "right-0" : "left-0"),
                                                                fixed && renderSubComponent && align !== "right" && "left-12",
                                                                highlightClass ? highlightClass.replace('border-l-4', '') :
                                                                    fixed ? "bg-white dark:bg-gray-900" : ""
                                                            )}
                                                            style={
                                                                !fixed && isFirstNonFixedColumn(colIndex) && highlightClass.includes('border-l-4')
                                                                    ? {
                                                                        borderLeft: '4px solid #10B981'
                                                                    }
                                                                    : {}
                                                            }
                                                        >
                                                            {Cell ? <Cell value={row[accessor]} row={row} /> : row[accessor]}
                                                        </td>
                                                    ))}
                                                </tr>
                                                {renderSubComponent && expandedRows[i] && (
                                                    <tr className={cn(
                                                        "bg-gray-50 dark:bg-gray-800",
                                                        highlightClass ? highlightClass.replace('border-l-4', '') : ""
                                                    )}>
                                                        <td colSpan={columns.length + 1} className="p-0">
                                                            {renderSubComponent({ row })}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={columns.length + (renderSubComponent ? 1 : 0)}
                                            className="text-center py-12 sm:py-16"
                                        >
                                            <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                                                <EmptyIcon />
                                                <h3 className="mt-4 text-sm sm:text-lg font-semibold">
                                                    No Records Found
                                                </h3>
                                                <p className="text-xs sm:text-sm">
                                                    There is no data matching your query.
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )
            }

            {/* Pagination */}
            {
                pageCount > 0 && !loading && (
                    <div className="px-3 sm:px-6 py-3 flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center sm:justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                        {/* Mobile: Prev / Next only */}
                        <div className="flex sm:hidden justify-between">
                            <button
                                onClick={() =>
                                    setPagination((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))
                                }
                                disabled={pagination.pageIndex === 0}
                                className="px-4 py-2 border rounded-md text-xs font-medium bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() =>
                                    setPagination((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))
                                }
                                disabled={pagination.pageIndex >= pageCount - 1}
                                className="px-4 py-2 border rounded-md text-xs font-medium bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>

                        {/* Tablet + Desktop */}
                        <div className="hidden sm:flex flex-1 items-center justify-between">
                            {/* Rows per page */}
                            <div className="flex items-center gap-x-2">
                                <span className="text-xs text-gray-700 dark:text-gray-300">
                                    Rows per page
                                </span>
                                <select
                                    value={pagination.pageSize}
                                    onChange={handlePageSizeChange}
                                    className="block rounded-md p-1 border-gray-300 dark:border-gray-600 shadow-md focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-xs dark:bg-gray-800 dark:text-gray-100"
                                >
                                    {[10, 20, 50, 100].map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Page info + navigation */}
                            <div className="flex items-center gap-x-4 lg:gap-x-6">
                                <p className="text-xs text-gray-700 dark:text-gray-300">
                                    Page{" "}
                                    <span className="font-medium">{pagination.pageIndex + 1}</span> of{" "}
                                    <span className="font-medium">{pageCount}</span>
                                </p>

                                {/* Jump to page (desktop only) */}
                                <form
                                    onSubmit={handleJumpToPage}
                                    className="hidden lg:flex items-center gap-x-2"
                                >
                                    <label
                                        htmlFor="jump-to-page"
                                        className="text-xs text-gray-700 dark:text-gray-300"
                                    >
                                        Go to page:
                                    </label>
                                    <input
                                        id="jump-to-page"
                                        type="number"
                                        value={jumpToPage}
                                        onChange={(e) => setJumpToPage(e.target.value)}
                                        className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 dark:bg-gray-800 dark:text-gray-100"
                                        min="1"
                                        max={pageCount}
                                    />
                                    <button
                                        type="submit"
                                        className="px-3 py-1 border rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-sidebar-primary dark:hover:bg-indigo-700 hover:bg-gray-100 focus:outline-none"
                                    >
                                        Go
                                    </button>
                                </form>

                                {/* Navigation buttons */}
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => setPagination((p) => ({ ...p, pageIndex: 0 }))}
                                        disabled={pagination.pageIndex === 0}
                                        className="px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronsLeftIcon />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setPagination((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))
                                        }
                                        disabled={pagination.pageIndex === 0}
                                        className="px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronLeftIcon />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setPagination((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))
                                        }
                                        disabled={pagination.pageIndex >= pageCount - 1}
                                        className="px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronRightIcon />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setPagination((p) => ({ ...p, pageIndex: pageCount - 1 }))
                                        }
                                        disabled={pagination.pageIndex >= pageCount - 1}
                                        className="px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                    >
                                        <ChevronsRightIcon />
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};